import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { CreditCardOutlined, KeyOutlined, LogoutOutlined, PersonOutline, StarBorder } from "@mui/icons-material";
import { BRAND, BODY_FONT } from "../theme";
import useCurrentUser, { logOut } from "./useCurrentUser";
import { CHANGE_PASSWORD_PATH, LOGIN_PATH, PROFILE_PATH, YOUR_SUBSCRIPTION_PATH } from "./accountRules";
import { SAVED_LISTINGS_PATH } from "./listingRules";
import useCredits from "./useCredits";
import { creditsLine } from "./creditRules";

// The user icon for the top right of a header, with a menu to My reports
// (saved listings), the profile and to log out. children go before the icon; when no one is logged in,
// signedOut is shown in place of both (just children while the server is asked).
// The link to log in for a header inside .lp: the words on wide screens, or with
// icon, a person icon for the far right on phones (.hide-sm / .show-sm in
// landing/Landing.css), like the map's header
export const LogInLink = ({icon=false}) => icon
    ? <Link className="show-sm person" to={LOGIN_PATH} aria-label="Log in"><PersonOutline/></Link>
    : <Link className="hide-sm" to={LOGIN_PATH}>Log in</Link>;

const UserMenu = ({children=null, signedOut=null, size=34}) => {
    const user = useCurrentUser();
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);
    // The usage line, asked for once the menu is first opened
    const [opened, setOpened] = useState(false);
    const plan = useCredits(opened && !!(user && user.signedin));
    const usage = plan && plan.credits ? creditsLine(plan.credits) : null;

    if (!user) return children;
    if (!user.signedin) return signedOut;

    const initial = (user.email || '?').charAt(0).toUpperCase();
    const close = () => setAnchor(null);

    return <>
        {children}
        <Tooltip title={user.email}>
            <IconButton onClick={(e) => { setOpened(true); setAnchor(e.currentTarget); }} aria-label="Your account" aria-haspopup="menu"
                aria-expanded={!!anchor} sx={{p:0.25}}>
                <Avatar sx={{width:size, height:size, bgcolor:BRAND.green, color:'#fff', fontFamily:BODY_FONT, fontWeight:600,
                    fontSize:size*0.45}}>{initial}</Avatar>
            </IconButton>
        </Tooltip>
        {/* No scroll lock: hiding the page's scrollbar while the menu is open shifted the whole page sideways */}
        <Menu anchorEl={anchor} open={!!anchor} onClose={close} disableScrollLock
            anchorOrigin={{vertical:'bottom', horizontal:'right'}} transformOrigin={{vertical:'top', horizontal:'right'}}>
            <MenuItem disabled sx={{opacity:'1 !important'}}>
                <ListItemText primary="Logged in as" secondary={user.email}
                    primaryTypographyProps={{fontSize:12, color:BRAND.mute}} secondaryTypographyProps={{color:BRAND.ink, fontSize:14}}/>
            </MenuItem>
            {usage &&
                <MenuItem disabled sx={{opacity:'1 !important', whiteSpace:'normal', maxWidth:280}}>
                    <ListItemText primary={usage} primaryTypographyProps={{fontSize:13, color:BRAND.mute}}/>
                </MenuItem>}
            <Divider/>
            <MenuItem onClick={() => { close(); navigate(SAVED_LISTINGS_PATH); }}>
                <ListItemIcon><StarBorder sx={{color:BRAND.green}}/></ListItemIcon>
                <ListItemText>My reports</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { close(); navigate(PROFILE_PATH); }}>
                <ListItemIcon><PersonOutline sx={{color:BRAND.green}}/></ListItemIcon>
                <ListItemText>Your profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { close(); navigate(YOUR_SUBSCRIPTION_PATH); }}>
                <ListItemIcon><CreditCardOutlined sx={{color:BRAND.green}}/></ListItemIcon>
                <ListItemText>Your subscription</ListItemText>
            </MenuItem>
            {user.logintype === 'standard' &&
                <MenuItem onClick={() => { close(); navigate(CHANGE_PASSWORD_PATH); }}>
                    <ListItemIcon><KeyOutlined sx={{color:BRAND.green}}/></ListItemIcon>
                    <ListItemText>Change password</ListItemText>
                </MenuItem>}
            <MenuItem onClick={() => { close(); logOut().then(() => navigate('/')); }}>
                <ListItemIcon><LogoutOutlined sx={{color:BRAND.green}}/></ListItemIcon>
                <ListItemText>Log out</ListItemText>
            </MenuItem>
        </Menu>
    </>;
};

export default UserMenu;
